import { useState, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react';
import { GqlWarrantProgram, GqlWiseDependencyBond } from '../../../../../graphql/base-types.graphql';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Typography } from '@startuptools/ui/joy/Typography';
import { Alert } from '@startuptools/ui/joy/Alert';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { RadioGroup } from '@startuptools/ui/joy/RadioGroup';
import { Radio } from '@startuptools/ui/joy/Radio';
import { FormLabel } from '@startuptools/ui/joy/FormLabel';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { Table } from '@startuptools/ui/joy/Table';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { useCompanyId } from '@startuptools/common/react-wrapper/CompanyIdProvider';
import { useWarrantProgramSuspenseQuery } from '../../../WarrantProgram.graphql';
import { ModalDialog } from '@startuptools/ui/joy/ModalDialog';
import { ModalClose } from '@startuptools/ui/joy/ModalClose';
import { Modal } from '@startuptools/ui/joy/Modal';
import { Button } from '@startuptools/ui/joy/Button';
import { Box } from '@startuptools/ui/Box';
import { Input } from '@startuptools/ui/Input';
import { DialogContent } from '@startuptools/ui/joy/DialogContent';
import { useStepNewSharesQuotaSuspenseQuery } from './StepWiseShares.graphql';
import { DateTime } from 'luxon';
import {
  useSnrBondsSuspenseQuery,
  useWiseDependencyBondsSuspenseQuery,
  useWpSetWiseFullyDilutedMutation,
  useWpSetNumberOfSharesMutation,
  WiseDependencyBondsDocument,
} from '../../../../../graphql/react-operations';
import {
  useCreateWiseDepBondMutation,
  useCreateWdbFromBondMutation,
  useEditWiseDependencyBondMutation,
  useDeleteWiseDependencyBondMutation,
} from '../../../../../graphql/react-operations';
import { isNil } from 'lodash-es';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@startuptools/ui/joy/IconButton';
import { Tooltip } from '@startuptools/ui/joy/Tooltip';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { toast } from '@startuptools/ui/toast/use-toast';
import { captureException } from '@sentry/react';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Checkbox } from '@startuptools/ui/joy/Checkbox';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';

type FormValues = {
  fullyDiluted: boolean;
  registeredShares: number;
};

type AddSharesFormValues = {
  shareName: string;
  shareAmount: number;
};

type WiseStepBond = {
  name: string;
  quantity: number;
  repurchased: number;
  total: number;
  wiseDepBondId?: string;
  snrBondId?: string;
};

export const StepWiseSharesNgWrap = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId }, fetchPolicy: 'cache-and-network' });
  return <StepWiseShares warrantProgram={warrantProgram} />;
};

export const StepWiseShares = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    'id' | 'wiseDepBondsRecordDay' | 'warrantStartDate' | 'wiseFullyDiluted' | 'valuationNumberOfShares'
  >;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  const [showAddSharesDialog, setShowAddSharesDialog] = useState<boolean>(false);
  const [selectedBond, setSelectedBond] = useState<WiseStepBond | null>(null);

  const recordDay = DateTime.fromISO(
    warrantProgram.wiseDepBondsRecordDay ?? warrantProgram.warrantStartDate ?? DateTime.now().toISO(),
  ).startOf('day');

  const {
    data: { snrNumberOfSharesAtDate },
  } = useStepNewSharesQuotaSuspenseQuery({
    variables: { companyId, date: recordDay.toISO() ?? '' },
    fetchPolicy: 'cache-and-network',
  });

  const { data: snrBondsData } = useSnrBondsSuspenseQuery({ variables: { companyId } });

  const { data: wiseDepBondsData } = useWiseDependencyBondsSuspenseQuery({
    variables: { companyId, warrantProgramId: warrantProgram.id },
    fetchPolicy: 'cache-and-network',
  });

  // eslint-disable-next-line sut/loading-mutation
  const [doSubmitWpSetWiseFullyDiluted] = useWpSetWiseFullyDilutedMutation({
    refetchQueries: [WiseDependencyBondsDocument],
  });
  const [doSubmitWpSetNumberOfShares, { loading }] = useWpSetNumberOfSharesMutation({
    refetchQueries: [WiseDependencyBondsDocument],
    awaitRefetchQueries: true,
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreateWiseDepBond] = useCreateWiseDepBondMutation({
    refetchQueries: [WiseDependencyBondsDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreateWdbFromBond] = useCreateWdbFromBondMutation({
    refetchQueries: [WiseDependencyBondsDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doEditWiseDependencyBond] = useEditWiseDependencyBondMutation({
    refetchQueries: [WiseDependencyBondsDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteWiseDependencyBond] = useDeleteWiseDependencyBondMutation({
    refetchQueries: [WiseDependencyBondsDocument],
  });

  const { wiseStepBonds, totalAmount } = useMemo(() => {
    if (!snrBondsData || !wiseDepBondsData) return { wiseStepBonds: [], totalAmount: 0 };

    const snrBonds = snrBondsData.snrBonds ?? [];
    const wiseDepBonds = wiseDepBondsData.wiseDependencyBonds ?? [];

    const relevantBonds = snrBonds
      .filter(b => b.decisionDate && b.shareSubEndDate)
      .filter(
        b =>
          DateTime.fromISO(b.decisionDate ?? '').startOf('day') <= recordDay &&
          DateTime.fromISO(b.shareSubEndDate ?? '').startOf('day') >= recordDay,
      )
      .sort(
        (a, b) => DateTime.fromISO(b.decisionDate ?? '').toMillis() - DateTime.fromISO(a.decisionDate ?? '').toMillis(),
      );

    const row: WiseStepBond[] = wiseDepBonds
      .filter(e => isNil(e.snrBondId))
      .map(b => ({
        name: b.name,
        quantity: Number(b.numberOfOptions),
        repurchased: 0,
        total: Number(b.numberOfOptions),
        wiseDepBondId: b.id,
      }));

    relevantBonds.forEach(b => {
      const wdb = wiseDepBonds.find(e => e.snrBondId === b.id);
      const repurchased = wdb ? Number(b.numberOfOptions) - Number(wdb.numberOfOptions) : 0;

      row.push({
        name: b.bondType,
        quantity: Number(wdb?.numberOfOptions ?? b.numberOfOptions),
        repurchased,
        total: Number(wdb?.numberOfOptions ?? b.numberOfOptions),
        snrBondId: b.id,
        wiseDepBondId: wdb?.id,
      });
    });

    return {
      wiseStepBonds: row,
      totalAmount: row.filter(bond => bond.wiseDepBondId).reduce((acc, row) => acc + row.total, 0),
    };
  }, [snrBondsData, wiseDepBondsData, recordDay]);

  const defaultWiseFullyDiluted = warrantProgram.wiseFullyDiluted ?? false;
  const defaultRegisteredShares = useMemo(() => {
    return Number(warrantProgram.valuationNumberOfShares ?? snrNumberOfSharesAtDate ?? 0);
  }, [warrantProgram.valuationNumberOfShares, snrNumberOfSharesAtDate]);

  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: { fullyDiluted: defaultWiseFullyDiluted, registeredShares: defaultRegisteredShares },
  });

  const showTableArea = watch('fullyDiluted');
  const registeredShares = watch('registeredShares');

  const handleDeleteWiseDepBondRow = async (wiseStepBond: WiseStepBond) => {
    try {
      await doDeleteWiseDependencyBond({
        variables: { companyId, id: wiseStepBond.wiseDepBondId ?? '' },
      });
    } catch (error) {
      captureException(error);
      toast({
        title: <Trans id="Could not delete" />,
        severity: 'danger',
        description: (
          <Trans id="Something went wrong when deleting, please try again and contact support if problem persists" />
        ),
      });
    } finally {
      setSelectedBond(null);
    }
  };

  const handleToggleIncludedSnrBond = async (wiseStepBond: WiseStepBond) => {
    try {
      const { wiseDepBondId, snrBondId } = wiseStepBond;
      if (wiseDepBondId) {
        await doDeleteWiseDependencyBond({
          variables: { companyId, id: wiseDepBondId },
        });
      } else if (snrBondId) {
        await doCreateWdbFromBond({
          variables: {
            companyId,
            snrBondId,
            warrantProgramId: warrantProgram.id,
          },
        });
      }
    } catch (error) {
      captureException(error);
      toast({
        title: <Trans id="Could not save" />,
        severity: 'danger',
        description: (
          <Trans id="Something went wrong when saving, please try again and contact support if problem persists" />
        ),
      });
    }
  };

  const handleOnSaveWiseDepBond = async (
    data: Pick<GqlWiseDependencyBond, 'name' | 'kind' | 'numberOfOptions'> & { wiseDepBondId?: string },
  ) => {
    try {
      if (selectedBond) {
        await doEditWiseDependencyBond({
          variables: {
            companyId,
            input: {
              id: selectedBond.wiseDepBondId ?? '',
              name: data.name,
              numberOfOptions: data.numberOfOptions,
              kind: '',
            },
          },
        });
      } else {
        await doCreateWiseDepBond({
          variables: {
            companyId,
            input: {
              name: data.name,
              numberOfOptions: data.numberOfOptions,
              kind: '',
              warrantProgramId: warrantProgram.id,
            },
          },
        });
      }
    } catch (error) {
      captureException(error);
      toast({
        title: <Trans id="Could not save" />,
        severity: 'danger',
        description: (
          <Trans id="Something wen wrong when saving, please try again and contact support if problem persists" />
        ),
      });
    } finally {
      setShowAddSharesDialog(false);
      setSelectedBond(null);
    }
  };

  const handleSetWiseFullyDiluted = async (value: boolean) => {
    try {
      await doSubmitWpSetWiseFullyDiluted({
        variables: { companyId, input: { id: warrantProgram.id, wiseFullyDiluted: value } },
      });
    } catch (error) {
      captureException(error);
      toast({
        title: <Trans id="Could not save" />,
        severity: 'danger',
        description: (
          <Trans id="Something went wrong when saving, please try again and contact support if problem persists" />
        ),
      });
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSubmitWpSetNumberOfShares({
        variables: {
          companyId,
          input: { id: warrantProgram.id, valuationNumberOfShares: values.registeredShares.toString() },
        },
      });
      await stepNext();
    } catch (error) {
      captureException(error);
      toast({
        title: <Trans id="Could not save" />,
        severity: 'danger',
        description: (
          <Trans id="Something wen wrong when saving, please try again and contact support if problem persists" />
        ),
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3}>
          <Typography level="h4">
            <Trans id="Dilution" />
          </Typography>
          <Alert color="neutral">
            <Trans
              id="
In a WISE convertible, the price is calculated based on the total number of shares in the company. This can either be based on the actual number of shares currently existing or the number that will exist due to outstanding options and any ongoing share issuances. The latter is commonly referred to as the number of shares on a fully diluted basis. The standard practice is to include share issuances and option programs that exist on the record date in the calculation of the total number of shares."
            />
          </Alert>
          <Controller
            control={control}
            name="fullyDiluted"
            render={({ field }) => (
              <RadioGroup
                {...field}
                value={field.value.toString()}
                defaultValue={defaultWiseFullyDiluted.toString()}
                onChange={async e => {
                  const value = e.target.value === 'true';
                  await handleSetWiseFullyDiluted(value);
                  field.onChange(value);
                }}
              >
                <Radio value={false} label={<Trans id="Based on the current number of shares" />} />
                <Radio value label={<Trans id="Based on the fully diluted number of shares" />} />
              </RadioGroup>
            )}
          />

          <Controller
            control={control}
            name="registeredShares"
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <FormControl required>
                <FormLabel>
                  <Trans id="Number of shares in the company" />
                </FormLabel>
                <NumericFormatInput
                  {...field}
                  value={field.value}
                  lang={i18n.locale}
                  onChange={e => setValue('registeredShares', Number(e.target.value))}
                  sx={{ minWidth: 340, maxWidth: 340 }}
                />
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />

          {showTableArea && (
            <>
              <Typography level="title-sm">
                <Trans id="Shares that are not registered at Bolagsverket on the Record Day" />
              </Typography>

              <Table sx={{ maxWidth: 784 }} size="sm" variant="outlined">
                <thead>
                  <tr>
                    <th>
                      <Trans id="Included" />
                    </th>
                    <th>
                      <Trans id="Name" />
                    </th>
                    <th align="right">
                      <Trans id="Amount" />
                    </th>
                    <th align="right">
                      <Trans id="Repurchased" />
                    </th>
                    <th align="right">
                      <Trans id="Total" />
                    </th>
                    <th>
                      <Button type="button" onClick={() => setShowAddSharesDialog(true)}>
                        <Trans id="Add" />
                      </Button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {wiseStepBonds.map((wsb, index) => (
                    <tr key={index}>
                      <td>
                        {wsb.snrBondId && (
                          <Checkbox
                            checked={!!wsb.wiseDepBondId}
                            onChange={async () => await handleToggleIncludedSnrBond(wsb)}
                          />
                        )}
                      </td>
                      <td>{wsb.name}</td>
                      <td align="right">
                        <NumberFormat locale={i18n.locale} minimumFractionDigits={0}>
                          {wsb.quantity}
                        </NumberFormat>
                      </td>
                      <td align="right">
                        <NumberFormat locale={i18n.locale} minimumFractionDigits={0}>
                          {wsb.repurchased}
                        </NumberFormat>
                      </td>
                      <td align="right">
                        <NumberFormat locale={i18n.locale} minimumFractionDigits={0}>
                          {wsb.total}
                        </NumberFormat>
                      </td>
                      <td>
                        <ButtonGroup>
                          <Tooltip title={<Trans id="Edit" />}>
                            <IconButton
                              type={'button'}
                              color={'primary'}
                              variant={'outlined'}
                              onClick={() => {
                                setSelectedBond(wsb);
                                setShowAddSharesDialog(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {wsb.name && !wsb.snrBondId && (
                            <Tooltip title={<Trans id="Remove" />}>
                              <IconButton
                                type={'button'}
                                color={'danger'}
                                variant={'outlined'}
                                onClick={async () => handleDeleteWiseDepBondRow(wsb)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4}>
                      <Trans id="Fully diluted" />
                    </td>
                    <td colSpan={2}>
                      <strong>
                        <NumberFormat locale={i18n.locale} minimumFractionDigits={0}>
                          {totalAmount + registeredShares}
                        </NumberFormat>
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </>
          )}

          {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

          <Box>
            <Button type="submit" loading={loading}>
              <Trans id="Save and Continue" />
            </Button>
          </Box>
        </Stack>
      </form>
      {showAddSharesDialog && (
        <AddSharesDialog
          onClose={() => {
            setShowAddSharesDialog(false);
            setSelectedBond(null);
          }}
          onSave={handleOnSaveWiseDepBond}
          initialValues={
            selectedBond
              ? {
                  name: selectedBond.name,
                  numberOfOptions: selectedBond.quantity.toString(),
                  wiseDepBondId: selectedBond.wiseDepBondId,
                  kind: '',
                }
              : undefined
          }
        />
      )}
    </>
  );
};

const AddSharesDialog = ({
  onClose,
  onSave,
  initialValues,
}: {
  onClose: () => void;
  onSave: (data: Pick<GqlWiseDependencyBond, 'name' | 'kind' | 'numberOfOptions'> & { wiseDepBondId?: string }) => void;
  initialValues?: Pick<GqlWiseDependencyBond, 'name' | 'kind' | 'numberOfOptions'> & { wiseDepBondId?: string };
}) => {
  const { control, handleSubmit, reset } = useForm<AddSharesFormValues>({
    defaultValues: {
      shareName: initialValues?.name || '',
      shareAmount: Number(initialValues?.numberOfOptions) || 0,
    },
  });

  const onSubmitShares: SubmitHandler<AddSharesFormValues> = async values => {
    onSave({
      wiseDepBondId: initialValues?.wiseDepBondId,
      kind: '',
      name: values.shareName,
      numberOfOptions: values.shareAmount.toString(),
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: 532, minHeight: 592 }}>
        <ModalClose />
        <DialogContent>
          <Stack gap={3}>
            <Typography level="h4">
              <Trans id={initialValues ? 'Edit Shares' : 'Add Shares'} />
            </Typography>
            <form onSubmit={handleSubmit(onSubmitShares)}>
              <Stack gap={4}>
                <Controller
                  control={control}
                  name="shareName"
                  render={({ field }) => (
                    <FormControl required>
                      <FormLabel>
                        <Trans id="Name" />
                      </FormLabel>
                      <Input {...field} />
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="shareAmount"
                  render={({ field }) => (
                    <FormControl required>
                      <FormLabel>
                        <Trans id="Share Amount" />
                      </FormLabel>
                      <NumericFormatInput
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value))}
                        value={field.value}
                      />
                    </FormControl>
                  )}
                />
                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Box>
                    <Button type="button" variant="outlined" onClick={handleClose}>
                      <Trans id="Cancel" />
                    </Button>
                  </Box>
                  <Box>
                    <Button type="submit">
                      {initialValues ? <Trans id={'Save Changes'} /> : <Trans id={'Add Shares'} />}
                    </Button>
                  </Box>
                </Stack>
              </Stack>
            </form>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};
